/**
 * Mode course : projection de la position, recalcul et POI mis en évidence.
 * EF-CAL-007, EF-MOD-002, EF-MOD-003, EF-MOD-006, EF-MOD-007.
 *
 * La projection d'un POI relève de EF-CAL-006. La projection de la **position
 * courante** obéit à des règles distinctes, un parcours pouvant repasser au
 * même endroit et une position pouvant être bruitée.
 */

import { addSeconds, type LocalDateTime } from '../core/local-datetime';
import type { Poi, PoiId, Stop } from '../core/model';
import { SPEED_CEILING_KMH, type DisplayParameters, type ScheduleParameters } from '../core/parameters';
import { bearing, cardinal, distance, projectOnSegment, type Coordinate } from '../geo/geodesy';
import { coordinateAt, indexBeforeProgression, progressionAt, type ComputedTrack } from '../geo/track';
import { isPoiVisible } from './display';
import { orderedPoiLines, type PoiLineRef } from './order';
import { ridingTimeAt, type RidingTimeCurve } from './riding-time';
import { computeSchedule, type ScheduleResult } from './schedule';

/** Recul autorisé en amont de la dernière progression retenue, en mètres. */
export const WINDOW_BACKWARD_METERS = 1000;

/** Plancher de la borne avant de la fenêtre de projection, en mètres. */
export const WINDOW_FORWARD_FLOOR_METERS = 2000;

/** Marge de distance en deçà de laquelle deux candidats sont équivalents. */
export const AMBIGUITY_DISTANCE_METERS = 200;

/** Séparation de progression à partir de laquelle deux candidats sont distincts. */
export const AMBIGUITY_PROGRESSION_METERS = 1000;

/** Séparation maximale entre deux projections mutuellement cohérentes. */
export const JUMP_COHERENCE_METERS = 1000;

/** Seuil de présence sur le parcours, en mètres. */
export const ON_ROUTE_BASE_METERS = 200;

/** Plafond du seuil de présence sur le parcours, en mètres. */
export const ON_ROUTE_CAP_METERS = 1000;

/** Tolérance conservant brièvement le POI qui vient d'être atteint. */
export const HIGHLIGHT_TOLERANCE_METERS = 200;

/** Conditions de déclenchement du recalcul en mode course. */
export const RECOMPUTE_MIN_SECONDS = 60;
export const RECOMPUTE_MIN_PROGRESSION_METERS = 250;

export interface ProjectionCandidate {
  readonly progressionMeters: number;
  readonly distanceToRouteMeters: number;
}

/**
 * Détection de la présence sur le parcours — EF-MOD-006. Le seuil est le
 * maximum entre 200 m et deux fois l'incertitude horizontale annoncée, plafonné
 * à 1 km.
 */
export function onRouteThresholdMeters(accuracyMeters: number): number {
  return Math.min(ON_ROUTE_CAP_METERS, Math.max(ON_ROUTE_BASE_METERS, 2 * accuracyMeters));
}

export function isOnRoute(distanceToRouteMeters: number, accuracyMeters: number): boolean {
  return distanceToRouteMeters <= onRouteThresholdMeters(accuracyMeters);
}

/**
 * Minima locaux de la distance à la trace dans une fenêtre de progression.
 * Les candidats sont affinés par projection sur les segments adjacents et
 * fusionnés lorsqu'ils sont séparés de moins de 500 m.
 */
export function scanCandidates(
  track: ComputedTrack,
  position: Coordinate,
  fromProgressionMeters: number,
  toProgressionMeters: number,
): readonly ProjectionCandidate[] {
  if (track.count === 0) return [];

  const firstIndex = Math.max(0, indexBeforeProgression(track, Math.max(0, fromProgressionMeters)));
  const lastIndex = Math.min(track.count - 1, indexBeforeProgression(track, toProgressionMeters) + 1);
  if (lastIndex <= firstIndex) {
    return [refineAt(track, position, firstIndex)];
  }

  const distances = new Float64Array(lastIndex - firstIndex + 1);
  for (let i = firstIndex; i <= lastIndex; i += 1) {
    distances[i - firstIndex] = distance(position, coordinateAt(track, i));
  }

  const candidates: ProjectionCandidate[] = [];
  for (let i = firstIndex; i <= lastIndex; i += 1) {
    const current = distances[i - firstIndex] ?? Number.POSITIVE_INFINITY;
    const previous = i > firstIndex ? (distances[i - 1 - firstIndex] ?? Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;
    const next = i < lastIndex ? (distances[i + 1 - firstIndex] ?? Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;
    if (current <= previous && current <= next) candidates.push(refineAt(track, position, i));
  }

  if (candidates.length === 0) return [];

  // Fusion des candidats trop proches, par ancrage sur le premier.
  const sorted = [...candidates].sort((a, b) => a.progressionMeters - b.progressionMeters);
  const merged: ProjectionCandidate[] = [];
  let best: ProjectionCandidate | undefined;
  let anchor = Number.NaN;
  for (const candidate of sorted) {
    if (best === undefined || candidate.progressionMeters - anchor >= 500) {
      if (best !== undefined) merged.push(best);
      anchor = candidate.progressionMeters;
      best = candidate;
      continue;
    }
    if (candidate.distanceToRouteMeters < best.distanceToRouteMeters) best = candidate;
  }
  if (best !== undefined) merged.push(best);

  return merged;
}

function refineAt(track: ComputedTrack, position: Coordinate, index: number): ProjectionCandidate {
  let best: ProjectionCandidate = {
    progressionMeters: progressionAt(track, index),
    distanceToRouteMeters: distance(position, coordinateAt(track, index)),
  };

  for (const from of [index - 1, index]) {
    if (from < 0 || from + 1 >= track.count) continue;
    const projection = projectOnSegment(position, coordinateAt(track, from), coordinateAt(track, from + 1));
    if (projection.distance < best.distanceToRouteMeters) {
      best = {
        progressionMeters: progressionAt(track, from) + projection.along,
        distanceToRouteMeters: projection.distance,
      };
    }
  }
  return best;
}

export type ProjectionOutcome =
  | { readonly kind: 'accepted'; readonly candidate: ProjectionCandidate }
  /** Plusieurs brins également plausibles : l'application ne choisit pas. */
  | { readonly kind: 'ambiguous'; readonly candidates: readonly ProjectionCandidate[] }
  /** Saut hors fenêtre non encore confirmé par une seconde position cohérente. */
  | { readonly kind: 'pendingConfirmation'; readonly candidate: ProjectionCandidate };

export interface ProjectorState {
  readonly lastProgressionMeters: number | null;
  readonly lastAcceptedAtMs: number | null;
}

/**
 * Projette la position courante en tenant compte de la fenêtre de recherche,
 * de la levée d'ambiguïté au démarrage et de la confirmation des sauts.
 */
export class RaceProjector {
  private lastProgressionMeters: number | null;
  private lastAcceptedAtMs: number | null = null;
  private pendingJump: ProjectionCandidate | null = null;
  private furthestProgressionMeters = 0;

  constructor(initial: ProjectorState = { lastProgressionMeters: null, lastAcceptedAtMs: null }) {
    this.lastProgressionMeters = initial.lastProgressionMeters;
    this.lastAcceptedAtMs = initial.lastAcceptedAtMs;
    this.furthestProgressionMeters = initial.lastProgressionMeters ?? 0;
  }

  get progressionMeters(): number | null {
    return this.lastProgressionMeters;
  }

  /** Progression la plus avancée atteinte : le franchissement n'est jamais annulé. */
  get crossedProgressionMeters(): number {
    return this.furthestProgressionMeters;
  }

  /** Rouvre la recherche sur la totalité du parcours et réinitialise le franchissement. */
  reset(): void {
    this.lastProgressionMeters = null;
    this.lastAcceptedAtMs = null;
    this.pendingJump = null;
    this.furthestProgressionMeters = 0;
  }

  /** Retient explicitement une progression, après la levée d'une ambiguïté. */
  accept(candidate: ProjectionCandidate, timestampMs: number): void {
    this.lastProgressionMeters = candidate.progressionMeters;
    this.lastAcceptedAtMs = timestampMs;
    this.pendingJump = null;
    this.furthestProgressionMeters = Math.max(this.furthestProgressionMeters, candidate.progressionMeters);
  }

  project(track: ComputedTrack, position: Coordinate, timestampMs: number): ProjectionOutcome | null {
    const previous = this.lastProgressionMeters;

    if (previous === null) {
      // En l'absence de progression retenue, la fenêtre couvre la totalité du
      // parcours et la levée d'ambiguïté s'applique.
      const candidates = scanCandidates(track, position, 0, track.lengthMeters);
      const best = closest(candidates);
      if (best === null) return null;

      const ambiguous = candidates.filter(
        (candidate) =>
          candidate.distanceToRouteMeters - best.distanceToRouteMeters <= AMBIGUITY_DISTANCE_METERS &&
          Math.abs(candidate.progressionMeters - best.progressionMeters) > AMBIGUITY_PROGRESSION_METERS,
      );
      if (ambiguous.length > 0) {
        return { kind: 'ambiguous', candidates: [best, ...ambiguous] };
      }

      this.accept(best, timestampMs);
      return { kind: 'accepted', candidate: best };
    }

    const elapsedSeconds = this.lastAcceptedAtMs === null ? 0 : Math.max(0, (timestampMs - this.lastAcceptedAtMs) / 1000);
    const forward = Math.max(WINDOW_FORWARD_FLOOR_METERS, (SPEED_CEILING_KMH * 1000 * elapsedSeconds) / 3600);
    const windowFrom = previous - WINDOW_BACKWARD_METERS;
    const windowTo = previous + forward;

    const inWindow = closest(scanCandidates(track, position, windowFrom, windowTo));
    const global = closest(scanCandidates(track, position, 0, track.lengthMeters));
    if (global === null) return null;

    const globalIsInWindow =
      global.progressionMeters >= windowFrom && global.progressionMeters <= windowTo;

    if (globalIsInWindow && inWindow !== null) {
      this.accept(inWindow, timestampMs);
      return { kind: 'accepted', candidate: inWindow };
    }

    // Sortie de fenêtre : deux positions valides consécutives et mutuellement
    // cohérentes sont nécessaires pour accepter le saut.
    const pending = this.pendingJump;
    if (
      pending !== null &&
      Math.abs(pending.progressionMeters - global.progressionMeters) < JUMP_COHERENCE_METERS
    ) {
      this.accept(global, timestampMs);
      return { kind: 'accepted', candidate: global };
    }

    this.pendingJump = global;
    return { kind: 'pendingConfirmation', candidate: global };
  }
}

function closest(candidates: readonly ProjectionCandidate[]): ProjectionCandidate | null {
  let best: ProjectionCandidate | null = null;
  for (const candidate of candidates) {
    if (best === null || candidate.distanceToRouteMeters < best.distanceToRouteMeters) best = candidate;
  }
  return best;
}

/** Informations affichées lorsque la position est valide mais hors parcours. */
export interface OffRouteReport {
  readonly distanceMeters: number;
  readonly bearingDegrees: number;
  readonly cardinal: string;
  readonly progressionMeters: number;
  /** Vrai lorsque le point le plus proche est en avant de la dernière progression. */
  readonly ahead: boolean;
}

export function describeOffRoute(
  track: ComputedTrack,
  position: Coordinate,
  lastProgressionMeters: number | null,
): OffRouteReport | null {
  const nearest = closest(scanCandidates(track, position, 0, track.lengthMeters));
  if (nearest === null) return null;

  const index = indexBeforeProgression(track, nearest.progressionMeters);
  const target = coordinateAt(track, index);
  const heading = bearing(position, target);

  return {
    distanceMeters: nearest.distanceToRouteMeters,
    bearingDegrees: heading,
    cardinal: cardinal(heading),
    progressionMeters: nearest.progressionMeters,
    ahead: lastProgressionMeters === null || nearest.progressionMeters >= lastProgressionMeters,
  };
}

export interface RaceRecomputeInput {
  readonly curve: RidingTimeCurve;
  readonly schedule: ScheduleParameters;
  readonly progressionMeters: number;
  readonly now: LocalDateTime;
  readonly includeSleep: boolean;
}

/**
 * Recalcul en mode course — EF-MOD-002. La courbe de temps de roulage est
 * réutilisée sans être reconstruite : la position projetée donne une
 * progression, et l'ordonnancement est rejoué pour les seuls arrêts à venir.
 *
 * La prochaine pause régulière est planifiée un intervalle complet après
 * l'instant du recalcul : l'application ne connaît ni le temps de pédalage
 * réel, ni les pauses réellement prises.
 */
export function recomputeForRace(input: RaceRecomputeInput): ScheduleResult {
  return computeSchedule(input.curve, input.schedule, {
    includeSleep: input.includeSleep,
    startLocal: input.now,
    consumedRidingSeconds: ridingTimeAt(input.curve, input.progressionMeters),
    emitStartStop: false,
  });
}

/** Vrai lorsque les conditions de déclenchement du recalcul sont réunies. */
export function shouldRecompute(
  lastComputedAtMs: number | null,
  lastProgressionMeters: number | null,
  nowMs: number,
  progressionMeters: number,
): boolean {
  if (lastComputedAtMs === null || lastProgressionMeters === null) return true;
  if (nowMs - lastComputedAtMs >= RECOMPUTE_MIN_SECONDS * 1000) return true;
  return Math.abs(progressionMeters - lastProgressionMeters) >= RECOMPUTE_MIN_PROGRESSION_METERS;
}

export type HighlightOutcome =
  | { readonly kind: 'poi'; readonly line: PoiLineRef; readonly hiddenBefore: number }
  | { readonly kind: 'finish'; readonly stop: Stop | null }
  | { readonly kind: 'noPoi' }
  | { readonly kind: 'allExcludedOrHidden'; readonly excludedCount: number; readonly hiddenCount: number };

export interface HighlightInput {
  readonly pois: ReadonlyMap<PoiId, Poi>;
  readonly display: DisplayParameters;
  readonly crossedProgressionMeters: number;
  readonly stops: readonly Stop[];
}

/**
 * POI mis en évidence — EF-MOD-003. C'est le premier POI non exclu et non
 * dépassé dans l'ordre du parcours, désigné par l'un de ses **passages**.
 *
 * Un POI masqué par un filtre de catégorie ne peut pas être mis en évidence :
 * l'application retient alors le premier POI visible et signale qu'un ou
 * plusieurs POI sont masqués avant lui.
 */
export function highlightedLine(input: HighlightInput): HighlightOutcome {
  const threshold = input.crossedProgressionMeters - HIGHLIGHT_TOLERANCE_METERS;

  let totalPois = 0;
  let excludedCount = 0;
  for (const poi of input.pois.values()) {
    totalPois += 1;
    if (poi.userState.excluded) excludedCount += 1;
  }
  if (totalPois === 0) return { kind: 'noPoi' };

  const notExcluded = orderedPoiLines(input.pois, (poi) => !poi.userState.excluded);
  const visible = orderedPoiLines(input.pois, (poi) => isPoiVisible(poi, input.display));

  const upcomingVisible = visible.filter((line) => line.progressionMeters >= threshold);
  const upcomingAny = notExcluded.filter((line) => line.progressionMeters >= threshold);

  if (upcomingAny.length === 0) {
    // Toutes les lignes de POI sont dépassées : l'Arrivée devient l'élément
    // mis en évidence.
    return { kind: 'finish', stop: input.stops.find((stop) => stop.type === 'finish') ?? null };
  }

  const target = upcomingVisible[0];
  if (target === undefined) {
    const hiddenCount = upcomingAny.length;
    return { kind: 'allExcludedOrHidden', excludedCount, hiddenCount };
  }

  const hiddenBefore = upcomingAny.filter((line) => line.progressionMeters < target.progressionMeters).length;
  return { kind: 'poi', line: target, hiddenBefore };
}

/**
 * File des lignes de POI à venir, dans l'ordre du parcours — EF-IHM-003.
 *
 * Même règle de sélection que `highlightedLine` : ni exclues, ni masquées, ni
 * dépassées, avec la même tolérance de 200 m. La première ligne de la file est
 * donc exactement le POI mis en évidence, ce qui garantit que la première
 * carte du mode course et la ligne mise en évidence dans le roadbook désignent
 * toujours le même passage.
 */
export function orderedUpcoming(
  pois: ReadonlyMap<PoiId, Poi>,
  display: DisplayParameters,
  crossedProgressionMeters: number,
): readonly PoiLineRef[] {
  const threshold = crossedProgressionMeters - HIGHLIGHT_TOLERANCE_METERS;
  return orderedPoiLines(pois, (poi) => isPoiVisible(poi, display)).filter(
    (line) => line.progressionMeters >= threshold,
  );
}

/** Heure estimée à une progression, à partir d'un recalcul de course. */
export function raceArrival(result: ScheduleResult, progressionMeters: number): LocalDateTime {
  return result.estimatedArrival(progressionMeters);
}

/** Instant courant décalé d'une durée, pour les affichages du mode course. */
export function shiftLocal(instant: LocalDateTime, seconds: number): LocalDateTime {
  return addSeconds(instant, seconds);
}
